import axios from 'axios';

import getAuthSession from './auth';
import User, {
  Company,
  CompanyApplication,
  CompanyCertification,
  CompanyDocument,
  CompanyEmployee,
  Contact,
  Service,
} from '../interfaces/users.interface';
import {
  CompanyDetail,
  CompanyReference,
  CompanyTabListParams,
  PaginatedResponse,
} from '../interfaces/companyDetail.interface';

const BASE_URL = `${process.env.REACT_APP_BASE_API}/company-detail`;

async function authHeaders(): Promise<{ Authorization: string }> {
  const authSession = await getAuthSession();
  return { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` };
}

/**
 * Fetch the lightweight company profile header.
 */
export async function getCompanyDetail(companyId: number | string): Promise<CompanyDetail> {
  const response = await axios.get<CompanyDetail>(
    `${BASE_URL}/${companyId}/`,
    { headers: await authHeaders() },
  );
  return response.data;
}

/**
 * Fetch lightweight identity records (id/name/type/isPublished) for a set of
 * company ids — scoped server-side to companies the requester can access. Used
 * to label referenced companies (e.g. assessed vendors) without per-company
 * full fetches.
 */
export async function getCompanyReferences(ids: number[]): Promise<CompanyReference[]> {
  if (ids.length === 0) return [];
  const response = await axios.get<CompanyReference[]>(
    `${process.env.REACT_APP_BASE_API}/company-references/`,
    { headers: await authHeaders(), params: { ids: ids.join(',') } },
  );
  return response.data;
}

/**
 * Reconcile a company's site-level services to the services it currently offers
 * (server-side), so a site never advertises a dropped service. No client fetch
 * of every site is needed.
 */
export async function pruneCompanySiteServices(companyId: number | string): Promise<void> {
  await axios.post(
    `${BASE_URL}/${companyId}/prune-site-services/`,
    {},
    { headers: await authHeaders() },
  );
}

/**
 * Shared fetcher for the paginated per-tab list endpoints.
 */
async function getCompanyTab<T>(
  companyId: number | string,
  tab: string,
  params: CompanyTabListParams = {},
): Promise<PaginatedResponse<T>> {
  const response = await axios.get<PaginatedResponse<T>>(
    `${BASE_URL}/${companyId}/${tab}/`,
    { headers: await authHeaders(), params },
  );
  return response.data;
}

export function getCompanySites(
  companyId: number | string,
  params?: CompanyTabListParams,
): Promise<PaginatedResponse<Contact>> {
  return getCompanyTab<Contact>(companyId, 'sites', params);
}

export function getCompanyApps(
  companyId: number | string,
  params?: CompanyTabListParams,
): Promise<PaginatedResponse<CompanyApplication>> {
  return getCompanyTab<CompanyApplication>(companyId, 'apps', params);
}

export function getCompanyCertifications(
  companyId: number | string,
  params?: CompanyTabListParams,
): Promise<PaginatedResponse<CompanyCertification>> {
  return getCompanyTab<CompanyCertification>(companyId, 'certifications', params);
}

export function getCompanyServices(
  companyId: number | string,
  params?: CompanyTabListParams,
): Promise<PaginatedResponse<Service>> {
  return getCompanyTab<Service>(companyId, 'services', params);
}

export function getCompanyDocuments(
  companyId: number | string,
  params?: CompanyTabListParams,
): Promise<PaginatedResponse<CompanyDocument>> {
  return getCompanyTab<CompanyDocument>(companyId, 'documents', params);
}

export function getCompanyUsers(
  companyId: number | string,
  params?: CompanyTabListParams,
): Promise<PaginatedResponse<CompanyEmployee>> {
  return getCompanyTab<CompanyEmployee>(companyId, 'users', params);
}

export function getCompanyPendingUsers(
  companyId: number | string,
  params?: CompanyTabListParams,
): Promise<PaginatedResponse<User>> {
  return getCompanyTab<User>(companyId, 'pending-users', params);
}

export function getCompanySubsidiaries(
  companyId: number | string,
  params?: CompanyTabListParams,
): Promise<PaginatedResponse<Company>> {
  return getCompanyTab<Company>(companyId, 'subsidiaries', params);
}
